/**
 * Platform security settings (Platform admin console, pages/PlatformAdmin.jsx):
 * the file-scanning policy over GET/PUT /admin/security-settings - scanners,
 * verdict aggregation, fail mode and timeout. The server gate admits a
 * platform-admin or a `system:admin` holder (docs/Decisions.md D49). A save that
 * weakens protection (scanning off, failing open, no scanner) asks for
 * confirmation first, and the posture notes say what the form would do to
 * uploads before it is saved.
 */
import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../api/admin';
import { useConfirm } from '../lib/confirm';
import { useToast } from './toast';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  SelectField,
  UserChip,
  formatDateTime,
} from './ui';
import {
  AGGREGATIONS,
  FAIL_MODES,
  MAX_TIMEOUT_SECONDS,
  MIN_TIMEOUT_SECONDS,
  buildSecurityPayload,
  securityFormErrors,
  securityPostureNotes,
  securityToForm,
  toggleScanner,
  weakensProtection,
} from '../lib/securitySettings';
import { cn, ui } from '../lib/ui';

export default function SecuritySettingsPanel() {
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [record, setRecord] = useState(null); // effective settings incl. updatedAt/updatedBy
  const [scanners, setScanners] = useState([]);
  const [form, setForm] = useState(() => securityToForm({}));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const apply = data => {
    setRecord(data.settings);
    setScanners(data.availableScanners || []);
    setForm(securityToForm(data.settings));
  };

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    adminApi
      .getSecuritySettings()
      .then(apply)
      .catch(err => setLoadError(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = patch => setForm(current => ({ ...current, ...patch }));
  const errors = securityFormErrors(form, scanners);
  const notes = securityPostureNotes(form, scanners);
  // A stored id the catalogue no longer lists still gets a row, so it can be
  // unticked; otherwise the save would 400 with no way to fix it here.
  const rows = [
    ...scanners,
    ...form.scanners
      .filter(id => !scanners.some(s => s.id === id))
      .map(id => ({ id, displayName: id, unknown: true })),
  ];

  async function onSave(event) {
    event.preventDefault();
    if (errors.length) return;
    if (weakensProtection(record && securityToForm(record), form)) {
      const ok = await confirm({
        title: 'Weaken file scanning?',
        message: `${notes.join(' ')} This applies to every upload on the platform.`,
        confirmLabel: 'Save anyway',
        danger: true,
      });
      if (!ok) return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      apply(await adminApi.putSecuritySettings(buildSecurityPayload(form)));
      toast.success('Security settings saved.');
    } catch (err) {
      setSaveError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner />;
  if (loadError) {
    return (
      <div className={ui.card}>
        <ErrorAlert error={loadError} />
        <button type="button" className={ui.btnSecondary} onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSave} className={ui.card}>
      <ErrorAlert error={saveError} />
      <p className={cn(ui.muted, 'text-body-s')}>
        Malware scanning for uploaded files, including organization site packs, which are never
        published until they pass. Changes apply to the whole platform within a minute and are
        audit-logged.
      </p>

      <fieldset className="mt-4">
        <legend className={ui.label}>File scanning</legend>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={e => set({ enabled: e.target.checked })}
          />{' '}
          Scan uploaded files for malware
        </label>
      </fieldset>

      <fieldset className="mt-4" disabled={!form.enabled}>
        <legend className={ui.label}>Scanners</legend>
        <p className={cn(ui.muted, 'text-body-s')}>
          Applied in the order ticked. A scanner marked not deployed is selectable, but its
          infrastructure is missing in this environment, so its scans error.
        </p>
        {rows.length === 0 ? (
          <EmptyState message="No scanners are available." />
        ) : (
          rows.map(scanner => (
            <label key={scanner.id} className={cn(ui.row, 'text-body-s')}>
              <input
                type="checkbox"
                checked={form.scanners.includes(scanner.id)}
                onChange={e =>
                  set({ scanners: toggleScanner(form.scanners, scanner.id, e.target.checked) })
                }
              />{' '}
              {scanner.displayName || scanner.id}
              {form.scanners.includes(scanner.id) && form.scanners.length > 1 ? (
                <span className={ui.muted}>(#{form.scanners.indexOf(scanner.id) + 1})</span>
              ) : null}
              {scanner.unknown ? (
                <span className={ui.pillWarning}>No longer available</span>
              ) : scanner.configured === false ? (
                <span className={ui.pillWarning}>Not deployed</span>
              ) : null}
            </label>
          ))
        )}
      </fieldset>

      <fieldset className="mt-4" disabled={!form.enabled}>
        <legend className={ui.label}>Policy</legend>
        <SelectField
          label="How verdicts combine"
          hint="Only matters when more than one scanner is selected."
          value={form.aggregation}
          onChange={e => set({ aggregation: e.target.value })}
        >
          {AGGREGATIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="When a scan cannot complete"
          value={form.failMode}
          onChange={e => set({ failMode: e.target.value })}
        >
          {FAIL_MODES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <Field
          label="Scan timeout (seconds)"
          type="number"
          min={MIN_TIMEOUT_SECONDS}
          max={MAX_TIMEOUT_SECONDS}
          step={1}
          hint={`Between ${MIN_TIMEOUT_SECONDS} and ${MAX_TIMEOUT_SECONDS}. A scan that runs longer counts as one that cannot complete.`}
          value={form.timeoutSeconds}
          onChange={e => set({ timeoutSeconds: e.target.value })}
        />
      </fieldset>

      {notes.length > 0 && (
        <div className={cn(ui.alertWarning, 'mt-4')} role="status">
          <ul className="m-0 list-disc pl-5">
            {notes.map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <ul className={cn(ui.alertError, 'list-disc pl-8')}>
          {errors.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      <div className={cn(ui.row, 'mt-4')}>
        <button type="submit" className={ui.btnPrimary} disabled={saving || errors.length > 0}>
          {saving ? 'Saving…' : 'Save security settings'}
        </button>
      </div>

      {record && (record.updatedAt || record.updatedBy) && (
        <p className={cn(ui.muted, 'text-body-s mt-4')}>
          Last updated {record.updatedAt ? formatDateTime(record.updatedAt) : 'never'}
          {record.updatedBy && (
            <>
              {' '}
              by <UserChip userId={record.updatedBy} />
            </>
          )}
        </p>
      )}
    </form>
  );
}
